import { Box, Stack } from '@mantine/core';
import { UserRole } from '@repo/common/enums/UserRole';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
	queryClient,
	reportsQueryKeyFactory,
	useSuspenseFeatureAccess,
	useUpdateReport,
} from '../../api';
import EntityPageTitle from '../../components/EntityPageLayout/EntityPageTitle';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import { appShellStore } from '../../components/SecodaAppShell/store';
import { AutomationsOverviewTabController } from '../../features/Overview/AutomationsOverviewTabController';
import { useCheckAnalyticsWritePermission } from '../../utils/authorization/roles';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import { useReportManagement } from '../HomePage/hooks/useReportManagement';
import { DynamicReport } from './DynamicReport';
import { DataQualityReport } from './staticReports/DataQualityReport';
import type { IAnalyticsPageProps } from './types';

function AnalyticsPage({ id: propsId }: IAnalyticsPageProps) {
	const paramsId = useParamsIdSuffixUuid();
	const id = propsId || paramsId;
	const { analyticsAccess } = useSuspenseFeatureAccess();
	const { report } = useReportManagement({
		type: 'analytics',
		id,
	});

	const renderMainContent = useCallback(() => {
		if (report?.type === 'data_quality') {
			return <DataQualityReport />;
		}
		if (report?.type === 'd_automation') {
			return null;
		}
		return <DynamicReport id={id} />;
	}, [report?.type, id]);

	const readOnly = !useCheckAnalyticsWritePermission({ report });

	useEffect(() => {
		runInAction(() => {
			appShellStore.navBarUI.analyticsPage.showAddWidgetButton =
				report?.type === 'analytics';
			appShellStore.navBarUI.analyticsPage.downloadDataQualityScore.downloadButtonVisible =
				report?.type === 'data_quality';
			appShellStore.navBarUI.analyticsPage.reportId = report?.id || '';
			appShellStore.navBarUI.analyticsPage.reportTitle = report?.name || '';
		});
	}, [report]);

	const { mutateAsync: updateReport } = useUpdateReport({
		disableOptimisticUpdate: true,
		disableInvalidation: true,
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: reportsQueryKeyFactory.byArgs('analytics', id),
				});
				queryClient.invalidateQueries({
					queryKey: reportsQueryKeyFactory.allLists(),
				});
			},
		},
	});

	if (!report) {
		return <LoadingSpinner />;
	}

	return (
		<FeatureGuard
			featureName="Analytics"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Analytics.Read"
			isFeatureIncludedInPlan={analyticsAccess ?? false}
			isAlwaysIncludedFeature={false}
			description="Understand workspace usage, important KPIs, and more with analytics."
		>
			<PageLayoutOuterWrapper key={report.id}>
				<Helmet>
					<title>{report.name} - Analytics</title>
				</Helmet>
				<PageLayoutWrapper name="analytics">
					<PageLayoutContentWrapper name="analytics">
						<PageLayoutContent
							maw={report?.type === 'data_quality' ? 848 : undefined}
						>
							<Stack spacing="sm">
								<Box>
									<EntityPageTitle
										icon={null}
										placeholder={'Untitled'}
										entity={{
											title: report.name,
											title_cased: report.name,
										}}
										isReadOnly={readOnly}
										onChange={(value) =>
											updateReport({ data: { id: report.id, name: value } })
										}
										wrapTitle
									/>
								</Box>
								{report?.type === 'd_automation' && (
									<AutomationsOverviewTabController />
								)}
								<Box style={{ flex: 1 }}>{renderMainContent()}</Box>
							</Stack>
						</PageLayoutContent>
					</PageLayoutContentWrapper>
				</PageLayoutWrapper>
			</PageLayoutOuterWrapper>
		</FeatureGuard>
	);
}

const AnalyticsPageComponent = observer(AnalyticsPage);
export default AnalyticsPageComponent;
